import React from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useParams } from 'react-router-dom';
import Dashboard from './pages/Dashboard.tsx';
import SetSelection from './pages/SetSelection.tsx';
import Wishlist from './pages/Wishlist.tsx';
import Settings from './pages/Settings.tsx';
import PublicProfile from './pages/PublicProfile.tsx';
import SetHub from './pages/SetHub.tsx';
import CardHub from './pages/CardHub.tsx';
import Scanner from './pages/Scanner.tsx';
import Exchange from './pages/Exchange.tsx';
import { getProfile } from './lib/profile.ts';
import AuthDialog from './components/AuthDialog.tsx';
import SearchModal from './components/SearchModal.tsx';
import Binder from './App';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';

function BinderRoute() {
  const { setName } = useParams();
  if (setName) {
    try {
      localStorage.setItem('selectedSet', JSON.stringify(decodeURIComponent(setName)));
    } catch {}
  }
  return <Binder />;
}

function NavBar() {
  const { user, loading, signOut } = useAuth();
  const [handle, setHandle] = React.useState<string | null>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [authOpen, setAuthOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (!user) { setHandle(null); return; }
      try {
        const p = await getProfile(user.uid);
        setHandle(p?.handle || null);
      } catch { setHandle(null); }
    })();
  }, [user]);

  // Global keyboard shortcut for search
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
    <nav className="sticky top-0 z-50 p-3 bg-black/60 text-white backdrop-blur">
      <div className="max-w-6xl mx-auto flex items-center">
        <Link to="/" className="font-bold">Binder UI</Link>
        <div className="hidden md:flex gap-4 text-sm opacity-90 ml-6">
          <Link to="/browse">Browse Sets</Link>
          <Link to="/wishlist">Wishlist</Link>
          <Link to="/scan">Scan</Link>
          <Link to="/exchange">Exchange</Link>
          <button
            onClick={() => setSearchOpen(true)}
            className="hover:opacity-100 transition-opacity"
          >
            Search
          </button>
        </div>
        <button className="md:hidden ml-auto bg-white/20 px-3 py-1 rounded" onClick={()=> setMenuOpen((v)=>!v)}>Menu</button>
        <div className="hidden md:flex items-center gap-2 ml-auto">
          <button
            onClick={() => setSearchOpen(true)}
            className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded flex items-center gap-1"
            title="Search cards (Ctrl+K)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden lg:inline">⌘K</span>
          </button>
        {loading ? (
          <span className="opacity-70 text-sm">Loading…</span>
        ) : user ? (
          <div className="flex items-center gap-2">
            <Link to="/settings" className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">Settings</Link>
            {handle && <Link to={`/u/${handle}`} className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">View Profile</Link>}
            <button onClick={signOut} className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">
              Sign out {user.displayName ? `(${user.displayName})` : ''}
            </button>
          </div>
        ) : (
            <button onClick={()=> setAuthOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-1 rounded">Sign in</button>
        )}
        </div>
      </div>
      {menuOpen && (
        <div className="md:hidden mt-3 space-y-2">
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/browse" onClick={()=> setMenuOpen(false)}>Browse Sets</Link>
            <Link to="/wishlist" onClick={()=> setMenuOpen(false)}>Wishlist</Link>
            <Link to="/scan" onClick={()=> setMenuOpen(false)}>Scan Card</Link>
            <Link to="/exchange" onClick={()=> setMenuOpen(false)}>Exchange</Link>
            <button
              onClick={() => { setMenuOpen(false); setSearchOpen(true); }}
              className="text-left"
            >
              Search
            </button>
          </div>
          <div className="mt-2">
            {loading ? (
              <span className="opacity-70 text-sm">Loading…</span>
            ) : user ? (
              <div className="flex flex-col gap-2">
                <Link to="/settings" onClick={()=> setMenuOpen(false)} className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">Settings</Link>
                {handle && <Link to={`/u/${handle}`} onClick={()=> setMenuOpen(false)} className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">View Profile</Link>}
                <button onClick={()=> { setMenuOpen(false); signOut(); }} className="bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1 rounded">Sign out</button>
              </div>
            ) : (
              <button onClick={()=> { setMenuOpen(false); setAuthOpen(true); }} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-1 rounded">Sign in</button>
            )}
          </div>
        </div>
      )}
    </nav>
    <AuthDialog open={authOpen} onClose={()=> setAuthOpen(false)} />
    <SearchModal isOpen={searchOpen} onClose={()=> setSearchOpen(false)} />
    </>
  );
}

export default function Root() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NavBar />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/browse" element={<SetSelection />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/scan" element={<Scanner />} />
          <Route path="/exchange" element={<Exchange />} />
          <Route path="/u/:handle" element={<PublicProfile />} />
          <Route path="/set/:slug" element={<SetHub />} />
          <Route path="/card/:cardId" element={<CardHub />} />
          <Route path="/binder/:setName" element={<BinderRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}


